import { playSound, Sound } from '~/assets/sounds';
import { notifyOnError } from '~/rage/decorators';
import {
	ClientError,
	ClientErrorCode,
	HandlingMode,
	IClientConfigs,
	IPlayerVehicleListItem,
	PlayerVehicleSource,
	VehicleEngineType,
	VehicleHandling,
	VehicleHandlingWithModes,
	VehicleNumberPlate,
	VehicleType,
	deserializeHandling,
	serializeHandling,
	serializeHandlingWithModes,
} from '~/shared';
import { IObservable, Subject } from '../../utils';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { IRpcService } from '../RpcService';
import { VarsService } from '../VarsService/VarsService';
import {
	IVehicleService,
	IVehicleTurnLightsState,
} from './vehicleServiceTypes';

interface IVehicleData {
	id: string;
	source?: PlayerVehicleSource;
	vehicleNetId?: number;
	model: string;
	numberPlate: VehicleNumberPlate | null;

	isRegistered: boolean;
	isSpawned: boolean;
	isLocked: boolean;
	isFavorite?: boolean;
}

export class VehicleService implements IVehicleService {
	private playerVehicles = new Subject<IPlayerVehicleListItem[]>([]);
	private workVehicles = new Subject<IPlayerVehicleListItem[]>([]);
	private vehicleConfigsByModel = new Map<
		string,
		IClientConfigs['vehicles']['models'][number]
	>();
	private vehicleModels = new Subject<IClientConfigs['vehicles']['models']>([]);

	private localVehicleName = new Subject<string | null>(null);
	private pendingOperations = new Set<string>();

	constructor(
		private readonly rpc: IRpcService,
		private readonly vars: VarsService,
		private readonly gameConfig: GameConfigService,
		private readonly backend: BackendService,
	) {
		// Используем глобальные переменные, потому что значения меняются быстро
		(window as any)['carSpeedSubj'] = new Subject<number>(0);
		(window as any)['carRpmSubj'] = new Subject<number>(0);

		this.gameConfig
			.getConfigs()
			.subscribe(this.handleGameConfigChange.bind(this));

		this.vars
			.get('playerVehicles', [])
			.subscribe(this.handlePlayerVehiclesChange.bind(this));

		this.vars
			.get('workVehicles', [])
			.subscribe(this.handleWorkVehiclesChange.bind(this));

		this.getLocalVehicleModel().subscribe(
			this.handleLocalVehicleModelChange.bind(this),
		);

		let prevSeatbelt: boolean | null = null;
		this.getSeatbeltState().subscribe((isFastened) => {
			if (prevSeatbelt === null) {
				prevSeatbelt = isFastened;
				return;
			}
			if (prevSeatbelt === isFastened) return;
			prevSeatbelt = isFastened;
			if (!this.isPlayerInVehicle().getValue()) return;
			playSound(isFastened ? Sound.SeatbeltFasten : Sound.SeatbeltUnfasten);
		});

		let prevLights: boolean | null = null;
		this.getLocalVehicleLightsState().subscribe((isEnabled) => {
			if (prevLights === null) {
				prevLights = isEnabled;
				return;
			}
			if (prevLights === isEnabled) return;
			prevLights = isEnabled;
			if (!this.isPlayerInVehicle().getValue()) return;
			playSound(Sound.HeadlightsToggle);
		});
	}

	private handleGameConfigChange(configs: Partial<IClientConfigs>): void {
		if (!configs) return;

		this.vehicleConfigsByModel = new Map(
			configs.vehicles?.models?.map((model) => [model.model, model]) ?? [],
		);
		this.vehicleModels.next(configs.vehicles?.models ?? []);
	}

	public getPlayerVehicles(): IObservable<IPlayerVehicleListItem[]> {
		return this.playerVehicles;
	}

	private async handlePlayerVehiclesChange(
		ownedVehicles: IVehicleData[],
	): Promise<void> {
		await this.gameConfig.waitForInit();
		this.playerVehicles.next(this.processVehicleList(ownedVehicles));
	}

	private async handleWorkVehiclesChange(
		workVehicles: IVehicleData[],
	): Promise<void> {
		await this.gameConfig.waitForInit();
		this.workVehicles.next(this.processVehicleList(workVehicles));
	}

	private processVehicleList(
		vehicles: IVehicleData[],
	): IPlayerVehicleListItem[] {
		const processed: IPlayerVehicleListItem[] = vehicles.map((vehicle) => {
			const source = vehicle.source ?? PlayerVehicleSource.Owned;
			const name = this.getVehicleNameByModel(vehicle.model) ?? vehicle.model;
			const config = this.vehicleConfigsByModel.get(vehicle.model);
			const type = config?.type ?? VehicleType.CAR;
			const isFavorite =
				source === PlayerVehicleSource.Owned && vehicle.isFavorite === true;

			return {
				...vehicle,
				source,
				name,
				type,
				isFavorite,
			};
		});

		processed.sort((a, b) => {
			const nameComparison = a.name.localeCompare(b.name);
			if (nameComparison !== 0) {
				return nameComparison;
			}

			return a.id.localeCompare(b.id);
		});

		return processed;
	}

	private handleLocalVehicleModelChange(model: string | null): void {
		let nextValue: string | null = null;
		if (model !== null) {
			nextValue = this.getVehicleNameByModel(model);
		}

		this.localVehicleName.next(nextValue);
	}

	public getVehicleBrandNameByModel(model: string): string | null {
		const config = this.getModelConfig(model);
		return config?.brandName ?? null;
	}

	public getVehicleModelNameByModel(model: string): string | null {
		const config = this.getModelConfig(model);
		return config?.modelName ?? null;
	}

	public getVehicleNameByModel(model: string): string | null {
		const config = this.getModelConfig(model);
		return config?.name ?? null;
	}

	public getLocalVehicleName(): IObservable<string | null> {
		return this.localVehicleName;
	}

	public getLocalVehicleModel(): IObservable<string | null> {
		return this.vars.get('localVehicleModel', null);
	}

	public getLocalOwnedVehicleId(): IObservable<string | null> {
		return this.vars.get('localOwnedVehicleId', null);
	}

	public getLocalVehicleNetId(): IObservable<number | null> {
		return this.vars.get('localVehicleNetId', null);
	}

	public isLocalVehicleClientSide(): boolean {
		const vehicleNetId = this.getLocalVehicleNetId().getValue();
		return vehicleNetId === 65535;
	}

	public isPlayerInVehicle(): IObservable<boolean> {
		return this.vars.get('isPlayerInVehicle', false);
	}

	public isPlayerVehicleDriver(): IObservable<boolean> {
		return this.vars.get('isPlayerVehicleDriver', false);
	}

	public getLocalVehicleTurnLightsState(): IObservable<IVehicleTurnLightsState> {
		return this.vars.get('localVehicleTurnLights', {
			left: false,
			right: false,
		});
	}

	public getLocalVehicleMaxSpeed(): IObservable<number> {
		return this.vars.get('localVehicleMaxSpeed', 400);
	}

	/**
	 * Возвращает пробег текущего транспортного средства
	 */
	public getLocalVehicleMileage(): IObservable<number> {
		return this.vars.get('localVehicleMileage', 0);
	}

	/**
	 * Возвращает количество топлива в текущем транспортном средстве
	 */
	public getLocalVehicleFuel(): IObservable<number> {
		return this.vars.get('localVehicleFuel', 0);
	}

	/**
	 * Возвращает максимальную вместимость топливного бака текущего транспортного средства
	 */
	public getLocalVehicleFuelTankCapacity(): IObservable<number> {
		return this.vars.get('localVehicleFuelTankCapacity', 0);
	}

	/**
	 * Возвращает тип топлива текущего транспортного средства
	 */
	public getLocalVehicleEngineType(): IObservable<VehicleEngineType> {
		return this.vars.get('localVehicleEngineType', VehicleEngineType.Petrol);
	}

	/**
	 * Возвращает список транспорта, которым владеет игрок
	 */
	public getPlayerVehicleList(): IObservable<IPlayerVehicleListItem[]> {
		return this.playerVehicles;
	}

	/**
	 * Возвращает список рабочего транспорта игрока. Сейчас всегда максимум
	 * одна машина, но возвращаем массив на будущее.
	 */
	public getPlayerWorkVehicleList(): IObservable<IPlayerVehicleListItem[]> {
		return this.workVehicles;
	}

	public isVehiclePending(id: string): boolean {
		return this.pendingOperations.has(id);
	}

	private updateVehicleInList(
		id: string,
		updates: Partial<IPlayerVehicleListItem>,
	): void {
		this.playerVehicles.next(
			this.playerVehicles
				.getValue()
				.map((v) => (v.id === id ? { ...v, ...updates } : v)),
		);
		this.workVehicles.next(
			this.workVehicles
				.getValue()
				.map((v) => (v.id === id ? { ...v, ...updates } : v)),
		);
	}

	private getVehicleById(id: string): IPlayerVehicleListItem | null {
		return (
			this.playerVehicles.getValue().find((v) => v.id === id) ??
			this.workVehicles.getValue().find((v) => v.id === id) ??
			null
		);
	}

	public async setVehicleFavorite(id: string, state: boolean): Promise<void> {
		const vehicle = this.getVehicleById(id);
		if (vehicle?.source !== PlayerVehicleSource.Owned) return;

		await this.backend.invoke('vehicles:setFavorite', {
			vehicleId: id,
			favorite: state,
		});
		this.updateVehicleInList(id, { isFavorite: state });
	}

	public async spawnPlayerVehicle(id: string): Promise<void> {
		if (this.pendingOperations.has(id)) return;
		this.pendingOperations.add(id);

		try {
			const vehicle = this.getVehicleById(id);
			if (vehicle?.source === PlayerVehicleSource.Job) {
				await this.rpc.invokeServer('vehicles:moveWorkToPlayer');
				this.updateVehicleInList(id, { isSpawned: true });
				return;
			}

			if (!vehicle) {
				throw new ClientError(ClientErrorCode.VehicleNotFound);
			}

			await this.rpc.invokeServer('vehicles:spawn', {
				vehicleId: id,
			});

			this.updateVehicleInList(id, { isSpawned: true });
		} finally {
			this.pendingOperations.delete(id);
		}
	}

	public async destroyPlayerVehicle(id: string): Promise<void> {
		if (this.pendingOperations.has(id)) return;
		this.pendingOperations.add(id);

		try {
			const vehicle = this.getVehicleById(id);
			if (vehicle?.source === PlayerVehicleSource.Job) {
				return;
			}

			await this.rpc.invokeServer('vehicles:destroy', {
				vehicleId: id,
			});

			this.updateVehicleInList(id, { isSpawned: false });
		} finally {
			this.pendingOperations.delete(id);
		}
	}

	public async destroyAllPlayerVehicles(): Promise<void> {
		const workIds = new Set(this.workVehicles.getValue().map((v) => v.id));
		const workNetIds = new Set(
			this.workVehicles.getValue().map((v) => v.vehicleNetId),
		);
		this.playerVehicles.next(
			this.playerVehicles
				.getValue()
				.map((v) =>
					v.isSpawned &&
					!workIds.has(v.id) &&
					(v.vehicleNetId === undefined || !workNetIds.has(v.vehicleNetId))
						? { ...v, isSpawned: false }
						: v,
				),
		);

		await this.rpc.invokeServer('vehicles:destroyAll');
	}

	public async setPlayerVehicleLocked(
		id: string,
		state: boolean,
	): Promise<void> {
		if (this.pendingOperations.has(id)) return;
		this.pendingOperations.add(id);

		const previousLockState = this.getVehicleById(id)?.isLocked ?? false;
		this.updateVehicleInList(id, { isLocked: state });

		try {
			const vehicle = this.getVehicleById(id);
			if (vehicle?.source === PlayerVehicleSource.Job) {
				if (typeof vehicle.vehicleNetId !== 'number') {
					throw new ClientError(ClientErrorCode.VehicleNotFound);
				}

				await this.rpc.invokeServer('vehicles:setLockState', {
					vehicleNetId: vehicle.vehicleNetId,
					state,
				});
			} else {
				await this.rpc.invokeServer('vehicles:setLockStateById', {
					id,
					state,
				});
			}
		} catch (error) {
			this.updateVehicleInList(id, { isLocked: previousLockState });
			throw error;
		} finally {
			this.pendingOperations.delete(id);
		}
	}

	public getLocalVehicleSpeed(): IObservable<number> {
		return (window as any)['carSpeedSubj'];
	}

	public getLocalVehicleHandlingMode(): IObservable<HandlingMode> {
		return this.vars.get<HandlingMode>('handlingMode', HandlingMode.Comfort);
	}

	public getLocalVehicleVehicleType(): IObservable<VehicleType> {
		return this.vars.get<VehicleType>('localVehicleType', VehicleType.CAR);
	}

	@notifyOnError
	public async setLocalVehicleHandlingMode(mode: HandlingMode): Promise<void> {
		const vehicleNetId = this.getLocalVehicleNetId().getValue();
		return this.rpc.invokeServer<void>('vehicles:setHandlingMode', {
			mode,
			vehicleNetId,
		});
	}

	public async getLocalVehicleHandling(
		mode?: HandlingMode,
	): Promise<VehicleHandling | null> {
		if (this.isLocalVehicleClientSide()) {
			return null;
		}

		const vehicleNetId = this.getLocalVehicleNetId().getValue();
		const serialized = await this.rpc.invokeServer<VehicleHandling | null>(
			'vehicles:getHandling',
			{
				mode,
				vehicleNetId,
			},
		);
		return serialized ? deserializeHandling(serialized) : null;
	}

	public async getLocalVehicleStockHandling(): Promise<VehicleHandling> {
		const vehicleNetId = this.getLocalVehicleNetId().getValue();
		const serialized = await this.rpc.invokeClient<VehicleHandling>(
			'vehicles:getStockHandling',
			{
				vehicleNetId,
			},
		);
		return deserializeHandling(serialized);
	}

	@notifyOnError
	public async setLocalVehicleHandling(
		handling: VehicleHandling | VehicleHandlingWithModes,
		mode?: HandlingMode,
	): Promise<void> {
		const vehicleNetId = this.getLocalVehicleNetId().getValue();
		const serialized = Object.values(handling).includes(
			Object.values(HandlingMode)[0],
		)
			? serializeHandlingWithModes(handling as VehicleHandlingWithModes)
			: serializeHandling(handling as VehicleHandling);
		await this.rpc.invokeServer<void>('vehicles:setHandling', {
			mode,
			handling: serialized,
			vehicleNetId,
		});
	}

	public getLocalVehicleLockState(): IObservable<boolean> {
		return this.vars.get('localVehicleLockState', false);
	}

	public getLocalVehicleLightsState(): IObservable<boolean> {
		return this.vars.get('localVehicleLightsState', false);
	}

	public getSeatbeltState(): IObservable<boolean> {
		return this.vars.get('localPlayerSeatbeltState', false);
	}

	public getEngineState(): IObservable<boolean> {
		return this.vars.get('localVehicleEngineState', false);
	}

	public getLocalVehicleEngineRpm(): IObservable<number> {
		return (window as any)['carRpmSubj'];
	}

	/**
	 * Returns observable for all available vehicle models from config
	 */
	public getVehicleModels(): IObservable<IClientConfigs['vehicles']['models']> {
		return this.vehicleModels;
	}

	private getModelConfig(
		model: string,
	): IClientConfigs['vehicles']['models'][number] | null {
		return this.vehicleConfigsByModel.get(model) ?? null;
	}

	public getGovernmentPriceByModel(model: string): number {
		return (
			this.vehicleModels.getValue().find((v) => v.model === model)?.priceCash ??
			0
		);
	}
}
